import React, { useEffect } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import useSession, { useCustomer } from 'hooks/useSession'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { trpc } from 'api'
import FormInput from '@ui/forms/Input/FormInput'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { personalInfoStepSchema } from 'hooks/useIntroStore'
import { markDetailSectionSaved } from '@user/customer/confirmation/markDetailSectionSaved'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import { z } from 'zod'
import { BirthdateSchema, BirthdateValues } from '@ca/common'

interface Props {
  open: boolean
  close: () => void
}

export default function PersonalModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])
  const customer = useCustomer()

  const defaultValues = {
    first_name: customer?.first_name || undefined,
    last_name: customer?.last_name || undefined,
    birth_date: customer?.birth_date
      ? new Date(customer?.birth_date)
      : undefined,
  }

  const form = useForm<typeof defaultValues, any, typeof defaultValues>({
    defaultValues,
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> (birth_date: unknown via z.date())
    // as TFieldValues, mismatching typeof defaultValues (birth_date: Date|undefined). `as any`
    // preserves pre-7.55 behavior — Zod still validates at runtime.
    // See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(
      z.object({
        ...personalInfoStepSchema.shape,
        birth_date: BirthdateSchema,
      })
    ) as any,
  })

  const isChanged = form.formState.isDirty
  const styleChanged = useAnimatedOpacity(isChanged)

  const setCustomerData = useSession((s) => s.setCustomerData)
  const patch = trpc.customer.patch.useMutation({
    onSuccess: (res) => {
      setCustomerData(res)
      markDetailSectionSaved('personal')
      onClose()
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    if (!customer) return
    patch.mutate({
      id: customer.id,
      ...values,
      birth_date: values.birth_date?.toISOString().split('T')[0],
    })
  })

  const onClose = () => {
    form.reset()
    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:personal.title')}
      desc={t('modals:personal.desc')}
      body={
        <FormProvider {...form}>
          <View className="w-full gap-6 px-16">
            <FormInput
              control={form.control}
              label={t('user:firstName')}
              name="first_name"
              autoCorrect={false}
              autoCapitalize="words"
            />
            <FormInput
              control={form.control}
              label={t('user:lastName')}
              name="last_name"
              autoCorrect={false}
              autoCapitalize="words"
            />
            <FormInputDate
              control={form.control}
              label={t('user:birthDate')}
              name="birth_date"
              min={BirthdateValues.min}
              max={BirthdateValues.max}
              defaultDate={BirthdateValues.default}
            />
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button variant="outlined" label={t('ui:close')} onPress={onClose} />
          {isChanged && (
            <Button
              variant="filled"
              label={t('ui:save')}
              iconEnd="arrow-narrow-right"
              loading={patch.isPending}
              onPress={onSubmit}
              style={styleChanged}
            />
          )}
        </>
      }
    />
  )
}
